'use client'

import { cn } from '@/shared/lib'

import {
  GoToFirstPageButton,
  GoToLastPageButton,
  GoToNextPageButton,
  GoToPreviousPageButton,
} from './navigation-buttons'
import { PageButton, SelectPageMarker } from './page-button'
import { ButtonVariantsProps } from './variants'

const START_PAGE_NUMBER = 1
const DEFAULT_MAX_VISIBLE_PAGE_COUNT = 5

type Props = ButtonVariantsProps & {
  current?: number // 페이지 시작 번호 : 1
  total: number
  maxVisiblePageCount?: number
  onPageChange?: (page: number) => void
  className?: string
}

export const Pagination = ({
  mode = 'light',
  current = START_PAGE_NUMBER,
  total: totalProp,
  maxVisiblePageCount = DEFAULT_MAX_VISIBLE_PAGE_COUNT,
  onPageChange,
  className,
}: Props) => {
  const total = Math.max(totalProp, 1)

  const isSelected = (selectedPageNumber: number) =>
    current === selectedPageNumber

  const isFirstPage = current === 1
  const isLastPage = current === total

  const getPageRange = () => {
    const halfOfVisiblePageCount = Math.floor(maxVisiblePageCount / 2)

    // 현재 페이지(page)를 기준으로 보여질 페이지의 시작 번호를 계산
    // 시작 번호는 (현재 페이지 - 보여질 페이지 개수의 절반)과 시작 페이지(1) 중 큰 값으로 설정
    const start = Math.max(current - halfOfVisiblePageCount, START_PAGE_NUMBER)

    // 시작 번호를 기준으로 보여질 페이지의 마지막 번호를 계산
    // 마지막 번호는 (시작 번호 + 최대 보여질 페이지 개수 - 1)과 전체 페이지 수(total) 중 작은 값으로 설정
    const end = Math.min(start + maxVisiblePageCount - 1, total)

    if (end >= total) {
      return {
        start: Math.max(end - maxVisiblePageCount + 1, START_PAGE_NUMBER),
        end: total,
      }
    }

    if (start <= START_PAGE_NUMBER) {
      return {
        start: START_PAGE_NUMBER,
        end: Math.min(maxVisiblePageCount, total),
      }
    }

    return { start, end }
  }

  const visiblePages = (() => {
    const { start, end } = getPageRange()

    return Array.from({ length: end - start + 1 }, (_, index) => start + index)
  })()

  const selectedIndex = (() => {
    if (current > visiblePages[visiblePages.length - 1]) {
      return visiblePages.length - 1
    }

    if (current < visiblePages[0]) {
      return 0
    }

    return visiblePages.indexOf(current)
  })()

  const handlePageChange = (newPageNumber: number) => {
    onPageChange?.(newPageNumber)
  }

  return (
    <nav className={cn('flex items-center justify-center gap-3', className)}>
      <GoToFirstPageButton
        mode={mode}
        disabled={isFirstPage}
        onClick={() => {
          handlePageChange(1)
        }}
      />
      <GoToPreviousPageButton
        mode={mode}
        disabled={isFirstPage}
        onClick={() => {
          handlePageChange(current - 1)
        }}
      />

      <div className="relative flex items-center gap-3">
        {visiblePages.map(pageNumber => (
          <PageButton
            key={pageNumber}
            mode={mode}
            selected={isSelected(pageNumber)}
            onClick={() => {
              handlePageChange(pageNumber)
            }}
          >
            {pageNumber}
          </PageButton>
        ))}
        <SelectPageMarker mode={mode} selectedIndex={selectedIndex} />
      </div>

      <GoToNextPageButton
        mode={mode}
        disabled={isLastPage}
        onClick={() => {
          handlePageChange(current + 1)
        }}
      />
      <GoToLastPageButton
        mode={mode}
        disabled={isLastPage}
        onClick={() => {
          handlePageChange(total)
        }}
      />
    </nav>
  )
}
